import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DisplayMode, BodyCompositionStatus } from './types';

interface SegmentalMetricCardProps {
    label: string;
    valueKg?: number;
    valuePercentage?: number;
    displayMode?: DisplayMode;
}

const statusTranslationKeys: Record<BodyCompositionStatus, string> = {
    [BodyCompositionStatus.Low]: 'inbodyComposition.viewModal.statusLow',
    [BodyCompositionStatus.Average]: 'inbodyComposition.viewModal.statusAverage',
    [BodyCompositionStatus.High]: 'inbodyComposition.viewModal.statusHigh',
};

/**
 * Calculate status based on percentage value
 * - high: >= 110%
 * - average: 90% - 109%
 * - low: otherwise
 */
const getStatusFromPercentage = (percentage: number): BodyCompositionStatus => {
    if (percentage >= 110) {
        return BodyCompositionStatus.High;
    }
    if (percentage >= 90 && percentage <= 109) {
        return BodyCompositionStatus.Average;
    }
    return BodyCompositionStatus.Low;
};

const SegmentalMetricCard = ({
    label,
    valueKg,
    valuePercentage,
    displayMode = DisplayMode.Both,
}: SegmentalMetricCardProps) => {
    const { t } = useTranslation('physicalResults');
    const notAvailable = t('inbodyComposition.viewModal.notAvailable');

    const status = useMemo(
        () => (valuePercentage ? getStatusFromPercentage(valuePercentage) : undefined),
        [valuePercentage]
    );

    const formattedKg = valueKg ? t('inbodyComposition.viewModal.kgSuffix', { value: valueKg.toFixed(2) }) : undefined;

    const formattedPercentage = valuePercentage
        ? t('inbodyComposition.viewModal.percentageSuffix', { value: valuePercentage.toFixed(2) })
        : undefined;

    // Display both kg and percentage
    if (displayMode === DisplayMode.Both) {
        return (
            <div className="inline-flex flex-col items-center justify-start gap-0.5 self-stretch p-5">
                <div className="text-center text-xs leading-4 font-medium text-neutral-500">{label}</div>
                <div className="inline-flex items-start justify-start gap-2.5">
                    <div className="text-xl leading-7 font-bold text-neutral-900">{formattedKg || notAvailable}</div>
                    <div className="w-0.5 self-stretch bg-neutral-300" />
                    <div className="text-xl leading-7 font-bold text-neutral-900">
                        {formattedPercentage || notAvailable}
                    </div>
                </div>
                {status && (
                    <div className="text-center text-xs leading-4 font-bold text-teal-600">
                        {t(statusTranslationKeys[status])}
                    </div>
                )}
            </div>
        );
    }

    // Single value display (kg or percentage)
    const value = displayMode === DisplayMode.Kg ? formattedKg : formattedPercentage;
    return (
        <div className="flex w-full flex-col items-center gap-0.5 p-5">
            <div className="text-lg leading-7 font-bold text-neutral-900">{value || notAvailable}</div>
            <div className="text-center text-base leading-6 font-medium text-neutral-500">{label}</div>
            {status && (
                <div className="text-center text-base leading-6 font-bold text-teal-600">
                    {t(statusTranslationKeys[status])}
                </div>
            )}
        </div>
    );
};

export default SegmentalMetricCard;
